import React, { useState } from "react";
import { Link } from "react-router-dom";

import Button from "@/components/ui/Button";
import { applyConsent, getStoredConsent, isAnalyticsConfigured } from "@/lib/analytics";

/**
 * Minimal, GDPR-friendly cookie consent banner.
 *
 * Analytics are deny-by-default: GA only loads after the visitor clicks Accept.
 * The banner only appears when analytics are configured AND no choice has been
 * stored yet. Choice persists in localStorage, so it shows once.
 */
const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(
    () => isAnalyticsConfigured() && getStoredConsent() === null,
  );

  if (!visible) return null;

  const decide = (choice: "granted" | "denied") => {
    applyConsent(choice);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          We use privacy-friendly analytics cookies to understand site traffic.
          We never track the people who click your short links. See our{" "}
          <Link to="/privacy" className="font-medium text-foreground underline underline-offset-2">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" onClick={() => decide("denied")}>
            Decline
          </Button>
          <Button variant="brand" size="sm" onClick={() => decide("granted")}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;